import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Grid,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import axios from 'axios';
import api from '../../services/api';

const LarForm = ({ lar, onSubmit, readOnly }) => {
  const mounted = useRef(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    website: '',
    status: 'ativo',
    telefone1: '',
    telefone2: '',
    cep: '',
    endereco: '',
    bairro: '',
    numero: '',
    complemento: '',
    cidade: '',
    estado: '',
    observacoes: '',
    ...lar
  });

  const [estados, setEstados] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [loadingCep, setLoadingCep] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));

  const carregarCidades = useCallback(async (uf) => {
    try {
      const response = await axios.get(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`);
      const cidades = response.data.map(cidade => ({
        id: cidade.id,
        nome: cidade.nome
      }));
      setCidades(cidades);
      
      // Se a cidade atual não estiver na lista de cidades carregadas,
      // mantém o valor atual sem validação
      if (formData.cidade && !cidades.some(c => c.nome === formData.cidade)) {
        setFormData(prev => ({
          ...prev,
          cidade: formData.cidade
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar cidades:', error);
      setError('Erro ao carregar cidades. Por favor, tente novamente.');
    }
  }, [formData.cidade]);

  // Remover a configuração global do axios
  useEffect(() => {
    // Nada a fazer aqui, pois usaremos a instância api configurada
    // para chamadas internas e axios diretamente apenas para APIs externas
  }, []);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }

    const carregarDadosIniciais = async () => {
      if (lar?.id) {
        await carregarUsuarios(lar.id);
        if (lar.estado) {
          await carregarEstados();
        }
      }
    };

    carregarDadosIniciais();
  }, [lar]);

  useEffect(() => {
    if (formData.estado) {
      carregarCidades(formData.estado);
    }
  }, [formData.estado, carregarCidades]);

  const carregarEstados = async () => {
    try {
      const estados = [
        { uf: 'AC', nome: 'Acre' },
        { uf: 'AL', nome: 'Alagoas' },
        { uf: 'AP', nome: 'Amapá' },
        { uf: 'AM', nome: 'Amazonas' },
        { uf: 'BA', nome: 'Bahia' },
        { uf: 'CE', nome: 'Ceará' },
        { uf: 'DF', nome: 'Distrito Federal' },
        { uf: 'ES', nome: 'Espírito Santo' },
        { uf: 'GO', nome: 'Goiás' },
        { uf: 'MA', nome: 'Maranhão' },
        { uf: 'MT', nome: 'Mato Grosso' },
        { uf: 'MS', nome: 'Mato Grosso do Sul' },
        { uf: 'MG', nome: 'Minas Gerais' },
        { uf: 'PA', nome: 'Pará' },
        { uf: 'PB', nome: 'Paraíba' },
        { uf: 'PR', nome: 'Paraná' },
        { uf: 'PE', nome: 'Pernambuco' },
        { uf: 'PI', nome: 'Piauí' },
        { uf: 'RJ', nome: 'Rio de Janeiro' },
        { uf: 'RN', nome: 'Rio Grande do Norte' },
        { uf: 'RS', nome: 'Rio Grande do Sul' },
        { uf: 'RO', nome: 'Rondônia' },
        { uf: 'RR', nome: 'Roraima' },
        { uf: 'SC', nome: 'Santa Catarina' },
        { uf: 'SP', nome: 'São Paulo' },
        { uf: 'SE', nome: 'Sergipe' },
        { uf: 'TO', nome: 'Tocantins' }
      ];
      setEstados(estados);
    } catch (error) {
      console.error('Erro ao carregar estados:', error);
      setError('Erro ao carregar estados. Por favor, tente novamente.');
    }
  };

  const carregarUsuarios = async (larId) => {
    try {
      const response = await api.get(`/api/lares/${larId}/usuarios`);
      
      setUsuarios(response.data);
      setError(null); // Limpa qualquer erro anterior
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      
      // Verifica o tipo de erro
      if (error.response) {
        // Erro do servidor com resposta (400-500)
        if (error.response.status === 404) {
          // Não mostra erro 404 pois é esperado quando o lar é novo
          return;
        }
        if (error.response.status === 401) {
          setError('Sessão expirada. Por favor, faça login novamente.');
          // Limpar o token e redirecionar para login
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
        setError(`Erro ao carregar usuários: ${error.response.data.message}`);
      } else if (error.request) {
        // Erro de rede - servidor não respondeu
        setError('Erro de conexão. Verifique sua internet e tente novamente.');
      } else {
        // Erro de configuração da requisição
        setError('Erro ao carregar usuários. Por favor, tente novamente.');
      }
    }
  };

  const buscarCep = async (cep) => {
    if (!cep || cep.length !== 8) return;

    setLoadingCep(true);
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
      if (!response.data.erro) {
        // Carregar estados antes de atualizar o formulário
        await carregarEstados();
        const estado = estados.find(e => e.uf === response.data.uf);
        if (estado) {
          await carregarCidades(estado.uf);
        }
        setFormData(prev => ({
          ...prev,
          endereco: response.data.logradouro,
          bairro: response.data.bairro,
          cidade: response.data.localidade,
          estado: response.data.uf,
          complemento: response.data.complemento || prev.complemento
        }));
      } else {
        setError('CEP não encontrado. Por favor, verifique o número informado.');
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setError('Erro ao buscar CEP. Por favor, verifique o CEP informado.');
    } finally {
      setLoadingCep(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'cep') {
      const cepNumerico = value.replace(/\D/g, '');
      if (cepNumerico.length === 8) {
        buscarCep(cepNumerico);
      }
      setFormData(prev => ({ ...prev, [name]: cepNumerico }));
    } else if (name === 'telefone1' || name === 'telefone2') {
      // Remove tudo que não é número
      const numeroLimpo = value.replace(/\D/g, '');
      
      // Aplica a máscara
      let telefoneFormatado = '';
      
      // Limita a 11 dígitos (2 do país + 2 do DDD + 8 ou 9 do número)
      if (numeroLimpo.length <= 11) {
        // Adiciona o código do país
        if (numeroLimpo.length > 0) {
          telefoneFormatado = '+';
          if (numeroLimpo.length >= 2) {
            telefoneFormatado += numeroLimpo.substring(0, 2);
          } else {
            telefoneFormatado += numeroLimpo;
          }
        }
        
        // Adiciona o DDD
        if (numeroLimpo.length > 2) {
          telefoneFormatado += ' ' + numeroLimpo.substring(2, 4);
        }
        
        // Adiciona o número
        if (numeroLimpo.length > 4) {
          telefoneFormatado += ' ';
          // Se for celular (11 dígitos no total) ou fixo (10 dígitos no total)
          const isFixo = numeroLimpo.length <= 10;
          const posicaoHifen = isFixo ? 8 : 9;
          
          if (numeroLimpo.length > posicaoHifen) {
            telefoneFormatado += numeroLimpo.substring(4, posicaoHifen) + '-' + numeroLimpo.substring(posicaoHifen);
          } else {
            telefoneFormatado += numeroLimpo.substring(4);
          }
        }
      }
      
      setFormData(prev => ({ ...prev, [name]: telefoneFormatado }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await onSubmit(formData);
      setSuccess('Lar salvo com sucesso!');
      setTimeout(() => setSuccess(null), 3000); // Remove a mensagem após 3 segundos
    } catch (error) {
      console.error('Erro ao salvar lar:', error);
      setError(error.response?.data?.message || 'Erro ao salvar lar');
    } finally {
      setLoading(false);
    }
  };

  const canEditStatus = user?.profile === 'Administrador';

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Informações do Lar
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Nome"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            required
            disabled={readOnly}
            error={!formData.nome}
            helperText={!formData.nome && 'Campo obrigatório'}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="E-mail"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={readOnly}
            error={!formData.email}
            helperText={!formData.email && 'Campo obrigatório'}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Website"
            name="website"
            value={formData.website}
            onChange={handleChange}
            disabled={readOnly}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="Status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
            disabled={readOnly || !canEditStatus}
          >
            <MenuItem value="ativo">Ativo</MenuItem>
            <MenuItem value="suspenso">Suspenso</MenuItem>
            <MenuItem value="inativo">Inativo</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Telefone 1"
            name="telefone1"
            value={formData.telefone1}
            onChange={handleChange}
            required
            disabled={readOnly}
            error={!formData.telefone1}
            helperText={!formData.telefone1 ? 'Campo obrigatório' : 'Formato: +55 51 99999-8888'}
            inputProps={{
              maxLength: 17
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Telefone 2"
            name="telefone2"
            value={formData.telefone2}
            onChange={handleChange}
            disabled={readOnly}
            helperText="Formato: +55 51 99999-8888"
            inputProps={{
              maxLength: 17
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="CEP"
            name="cep"
            value={formData.cep}
            onChange={handleChange}
            required
            disabled={readOnly || loadingCep}
            error={!formData.cep}
            helperText={!formData.cep && 'Campo obrigatório'}
            InputProps={{
              endAdornment: loadingCep && <CircularProgress size={20} />,
            }}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Endereço"
            name="endereco"
            value={formData.endereco}
            onChange={handleChange}
            required
            disabled={readOnly}
            error={!formData.endereco}
            helperText={!formData.endereco && 'Campo obrigatório'}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            label="Número"
            name="numero"
            value={formData.numero}
            onChange={handleChange}
            required
            disabled={readOnly}
            error={!formData.numero}
            helperText={!formData.numero && 'Campo obrigatório'}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Complemento"
            name="complemento"
            value={formData.complemento}
            onChange={handleChange}
            disabled={readOnly}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Bairro"
            name="bairro"
            value={formData.bairro}
            onChange={handleChange}
            disabled={readOnly}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            select={!readOnly && estados.length > 0 ? true : false}
            label="Estado"
            name="estado"
            value={formData.estado || ''}
            onChange={handleChange}
            required
            disabled={readOnly}
            error={!formData.estado}
            helperText={!formData.estado && 'Campo obrigatório'}
          >
            {estados.length > 0 ? (
              estados.map((estado) => (
                <MenuItem key={estado.uf} value={estado.uf}>
                  {estado.nome}
                </MenuItem>
              ))
            ) : (
              <MenuItem value={formData.estado}>{formData.estado}</MenuItem>
            )}
          </TextField>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            select={!readOnly && formData.estado && cidades.length > 0 ? true : false}
            label="Cidade"
            name="cidade"
            value={formData.cidade || ''}
            onChange={handleChange}
            required
            disabled={readOnly || !formData.estado}
            error={!formData.cidade}
            helperText={!formData.cidade && 'Campo obrigatório'}
          >
            {formData.estado && cidades.length > 0 ? (
              cidades.map((cidade) => (
                <MenuItem key={cidade.id} value={cidade.nome}>
                  {cidade.nome}
                </MenuItem>
              ))
            ) : (
              <MenuItem value={formData.cidade}>{formData.cidade}</MenuItem>
            )}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Observações"
            name="observacoes"
            value={formData.observacoes}
            onChange={handleChange}
            disabled={readOnly}
          />
        </Grid>

        {lar?.createdAt && (
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary">
              Data de Criação: {lar.createdAt}
            </Typography>
          </Grid>
        )}

        {lar?.id && usuarios.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Usuários Vinculados
            </Typography>
            <List>
              {usuarios.map((usuario) => (
                <React.Fragment key={usuario.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={usuario.name}
                      secondary={`${usuario.email} - ${usuario.profile}`}
                    />
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              ))}
            </List>
          </Grid>
        )}

        {!readOnly && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default LarForm;
